/**
 * CartContext — single source of truth for the cart UI.
 *
 * Store cart (server-side, via Store API) is the source of truth for
 * products, prices, taxes, discounts, shipping — including Build Your Own
 * Box items, which are real WooCommerce products (SKUs build-box-8/12/24)
 * with the flavor selection carried as custom cart/order item data (see
 * addBuildBox below and the raretto-headless.php plugin's Build-a-Box
 * section).
 *
 * The hook shape exposed to the rest of the app:
 *  - cart, cartItems, cartLoading, cartError, refreshCart
 *  - addItem / updateItem / removeItem / applyCoupon / removeCoupon (Store API)
 *  - addBuildBox (resolves the right product by size, then behaves like addItem)
 *  - drawerOpen, setDrawerOpen (UI)
 *  - itemCount
 */
import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getCart,
  getProductBySku,
  addToCart as apiAddToCart,
  applyCoupon as apiApplyCoupon,
  removeCartItem as apiRemoveCartItem,
  removeCoupon as apiRemoveCoupon,
  updateCartItem as apiUpdateCartItem,
} from '@/lib/api/endpoints';
import { clearCartToken } from '@/lib/cartToken';

const CartContext = createContext(null);

const EMPTY_ITEMS = [];

export const CartProvider = ({ children }) => {
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const CART_KEY = ['cart'];

  // Store cart — server is source of truth
  const cartQuery = useQuery({
    queryKey: CART_KEY,
    queryFn: getCart,
    staleTime: 0, // every render reflects the latest server cart
  });

  const invalidateCart = () =>
    qc.invalidateQueries({ queryKey: CART_KEY });

  // Every Store API cart mutation already returns the full, authoritative
  // updated cart in its response — writing that straight into the query
  // cache reflects it immediately, instead of invalidating and waiting on
  // a second GET /cart round-trip just to re-fetch what we already have.
  const setCart = (data) => qc.setQueryData(CART_KEY, data);

  // Self-heal: if any cart mutation gets a 409 "Cart item no longer exists"
  // or the cart is missing, the Cart-Token in sessionStorage is stale (cart
  // expired on the server). Wipe it and refetch — the next request will mint
  // a fresh cart.
  const isStaleCartError = (err) => {
    if (!err) return false;
    if (err.status === 409) return true;
    if (err.status === 404 && err.code === 'woocommerce_rest_cart_invalid_key') return true;
    return false;
  };
  const handleStaleCart = () => {
    clearCartToken();
    invalidateCart();
  };

  // Shared rollback: snapshot the cart before an optimistic write so a
  // failed request can restore exactly what was there before, rather than
  // leaving the UI showing a change that never actually happened server-side.
  const snapshotCart = async () => {
    await qc.cancelQueries({ queryKey: CART_KEY });
    return qc.getQueryData(CART_KEY);
  };
  const rollbackCart = (context) => {
    if (context?.previousCart) setCart(context.previousCart);
  };

  const addMutation = useMutation({
    mutationFn: apiAddToCart,
    onSuccess: setCart,
    onError: (err) => { if (isStaleCartError(err)) handleStaleCart(); },
  });
  const updateMutation = useMutation({
    mutationFn: apiUpdateCartItem,
    // Optimistically reflect the new quantity the instant the user clicks,
    // rather than waiting on the network. We deliberately only touch the
    // `quantity` field here, not `totals` — recomputing tax/discount math
    // correctly on the client risks showing a subtly wrong number that
    // then visibly jumps once the real (correct) totals arrive. The
    // quantity itself is the thing the user is watching for feedback on;
    // the money figures settle a moment later when onSuccess replaces the
    // whole cart with the server's authoritative response.
    onMutate: async ({ key, quantity }) => {
      const previousCart = await snapshotCart();
      if (previousCart?.items) {
        setCart({
          ...previousCart,
          items: previousCart.items.map((item) =>
            item.key === key ? { ...item, quantity } : item
          ),
        });
      }
      return { previousCart };
    },
    onSuccess: setCart,
    onError: (err, vars, context) => {
      rollbackCart(context);
      if (isStaleCartError(err)) handleStaleCart();
    },
  });
  const removeMutation = useMutation({
    mutationFn: apiRemoveCartItem,
    // Safe to fully optimistic here — dropping the item from the list is
    // the whole signal the user is watching for, and it doesn't require
    // guessing at any tax/discount recalculation.
    onMutate: async (key) => {
      const previousCart = await snapshotCart();
      if (previousCart?.items) {
        setCart({
          ...previousCart,
          items: previousCart.items.filter((item) => item.key !== key),
        });
      }
      return { previousCart };
    },
    onSuccess: setCart,
    onError: (err, vars, context) => {
      rollbackCart(context);
      if (isStaleCartError(err)) handleStaleCart();
    },
  });
  const applyCouponMutation = useMutation({
    mutationFn: apiApplyCoupon,
    onSuccess: setCart,
    onError: (err) => { if (isStaleCartError(err)) handleStaleCart(); },
  });
  const removeCouponMutation = useMutation({
    mutationFn: apiRemoveCoupon,
    onSuccess: setCart,
    onError: (err) => { if (isStaleCartError(err)) handleStaleCart(); },
  });

  const cart = cartQuery.data;
  // Stable empty-array fallback so `cartItems` doesn't get a new reference
  // on every render (which would make the useMemo below recompute constantly).
  const cartItems = cart?.items ?? EMPTY_ITEMS;

  const itemCount = useMemo(
    () => cartItems.reduce((s, i) => s + (i.quantity || 0), 0),
    [cartItems]
  );

  // ---------- Store cart actions (async; return promises) ----------

  // Open the drawer immediately on click rather than waiting for the
  // network round-trip — the click gets instant visual acknowledgement,
  // and `addingToCart` lets the drawer show it's still working until the
  // real item lands a moment later.
  const addItem = (vars) => {
    setDrawerOpen(true);
    return addMutation.mutateAsync(vars);
  };

  const updateItem = updateMutation.mutateAsync;
  const removeItem = removeMutation.mutateAsync;
  const applyCoupon = applyCouponMutation.mutateAsync;
  const removeCoupon = removeCouponMutation.mutateAsync;

  // Resolves the real WooCommerce product for a box size by its SKU
  // (build-box-8/12/24 — see raretto-headless.php), then adds it to the
  // real cart with the flavor selection attached. Small in-memory cache
  // since the same size gets added repeatedly within a session and the
  // product ID never changes mid-session.
  const skuProductCache = useRef({});
  const addBuildBox = async ({ size, flavors }) => {
    const sku = `build-box-${size}`;
    let product = skuProductCache.current[sku];
    if (!product) {
      product = await getProductBySku(sku);
      if (product) skuProductCache.current[sku] = product;
    }
    if (!product) {
      throw new Error(
        `No "${sku}" product found in WooCommerce yet — create it in wp-admin first.`
      );
    }
    return addItem({ id: product.id, quantity: 1, flavors });
  };

  return (
    <CartContext.Provider
      value={{
        // Store cart
        cart,
        cartItems,
        cartLoading: cartQuery.isLoading,
        cartError: cartQuery.error,
        refreshCart: () => cartQuery.refetch(),
        addItem,
        addBuildBox,
        updateItem,
        removeItem,
        applyCoupon,
        removeCoupon,
        addingToCart: addMutation.isPending,

        // UI
        drawerOpen,
        setDrawerOpen,

        // Derived
        itemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used inside <CartProvider>');
  }
  return ctx;
};

export default CartContext;
